import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getTaskSubSov, addTaskSubSovLink, removeTaskSubSovLink } from '../api';
import ConfirmDialog from './ConfirmDialog';
import useConfirm from '../hooks/useConfirm';
import { numberLines } from '../lib/subSov';
import { moneyOfCents, centsFromText } from '../lib/subcontractors';

/**
 * Sub SOV links, in a sub's task window on the Subcontractors tab (subs/TaskWorkDialog.js; Duncan 2026-10-29: "the
 * process needs to be very similar to the project managers already existing deal ... We start with a task and then
 * link a schedule value to it" -- and there, not in the Schedule's Task Properties: "when the project managers [are]
 * setting up the schedule, there's a good chance that they will not know the subcontract stuff yet"): what the task's
 * sub bills E3 for it, as SOV links (TaskSOVLinks.js) say what E3 bills the owner. A task the sub is on (its Responsible Party)
 * takes a share of one of the sub's schedule-of-values lines on this job -- a percent of it or a dollar amount --
 * never more than the line has left. The same chip, picker, guard and remove as SOV links; the sub's lines are made
 * on Subcontractors → the sub → Billing. Saved as it's added or removed, as SOV links.
 *
 * Props:
 *   task                 Task object (.id, .subcontractor_id: the sub saved on it)
 *   onChanged            Optional. Called after every add/remove.
 *   onAddContract        Optional. With no subcontract yet: takes the PM to the sub's Documents, its Subcontract card
 *                        (Duncan 2026-10-29: "Add a link here to take you to that subcontractors document section").
 *   onAddLines           Optional. With no lines yet: takes the PM to the sub's Billing tab.
 */

const TrashIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
  </svg>
);
const PlusIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
);
const SearchIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" />
  </svg>
);
const AlertTriangleIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
    <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /><path d="M12 9v4M12 17h.01" />
  </svg>
);
const Spinner = ({ size = 13, color = '#fff' }) => (
  <span style={{ display: 'inline-block', width: size, height: size, borderRadius: 99, border: `2px solid color-mix(in oklch, ${color} 30%, transparent)`,
    borderTopColor: color, animation: 'spin .8s linear infinite', flexShrink: 0 }} />
);
const eyebrow = { display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 };
const note = { padding: '14px 12px', background: 'var(--bg-surface-2)', border: '1px dashed var(--border-default)', borderRadius: 8, fontSize: 14, color: 'var(--text-4, var(--text-3))', marginBottom: 4 };
const goLink = { border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', color: 'var(--accent-text)', fontSize: 14, fontWeight: 600, fontFamily: 'inherit' };

const TaskSubSovLinks = ({ task, onChanged, onAddContract, onAddLines }) => {
  const { confirm, confirmDialogProps } = useConfirm();
  const [data, setData] = useState(null); // { sub, has_contract, sections, lines, links }
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [error, setError] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const [lineId, setLineId] = useState('');
  const [method, setMethod] = useState('Percentage');
  const [amount, setAmount] = useState('100');
  const [search, setSearch] = useState('');
  const searchRef = useRef(null);

  const load = useCallback(async () => {
    if (!task?.id) return;
    try {
      setData(await getTaskSubSov(task.id));
      setError(null);
    } catch (e) {
      setError(e.message || 'Couldn’t load the sub’s SOV links');
    } finally {
      setLoading(false);
    }
  }, [task?.id]);
  useEffect(() => { setLoading(true); load(); }, [load, task?.subcontractor_id]);

  if (!task?.subcontractor_id && !(data && data.links.length)) return null;
  const subName = (data && data.sub && data.sub.name) || task.responsible_party || 'The sub';
  const { lines } = numberLines((data && data.lines) || [], (data && data.sections) || []);
  const numberOf = new Map(lines.map((l) => [l.id, l.number]));
  const q = search.trim().toLowerCase();
  const shown = lines.filter((l) => !q || `${l.number} ${l.description}`.toLowerCase().includes(q));
  const selected = lineId ? lines.find((l) => l.id === Number(lineId)) : null;
  const leftOf = (l) => l.value_cents - l.allocated_cents;
  const asked = !selected ? 0 : method === 'Percentage'
    ? Math.round((selected.value_cents * (parseFloat(amount) || 0)) / 100) : (centsFromText(amount) || 0);
  const over = selected && asked > leftOf(selected);
  const links = (data && data.links) || [];

  const reset = () => { setLineId(''); setSearch(''); setMethod('Percentage'); setAmount('100'); };
  const add = async () => {
    if (adding || !selected || !asked || over) return;
    setAdding(true); setError(null);
    try {
      await addTaskSubSovLink(task.id, method === 'Percentage'
        ? { line_id: selected.id, allocation_method: 'Percentage', allocation_percent: parseFloat(amount) }
        : { line_id: selected.id, allocation_method: 'Amount', allocation_amount_cents: centsFromText(amount) });
      reset();
      setShowPicker(false);
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      setError(e.message || 'Couldn’t link the line');
    } finally {
      setAdding(false);
    }
  };
  const remove = async (k) => {
    const ok = await confirm({
      title: 'Remove this sub SOV link?',
      message: `This task stops counting toward ${subName}’s line ${numberOf.get(k.line_id) || ''} (${moneyOfCents(k.allocation_amount_cents)}).`,
      confirmLabel: 'Remove link',
      danger: true,
    });
    if (!ok) return;
    setRemovingId(k.id); setError(null);
    try {
      await removeTaskSubSovLink(k.id);
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      setError(e.message || 'Couldn’t remove the link');
    } finally {
      setRemovingId(null);
    }
  };

  let body;
  if (loading) {
    body = <div style={{ ...note, display: 'flex', alignItems: 'center', gap: 8 }}><Spinner size={12} color="var(--accent)" /> Loading sub SOV links…</div>;
  } else if (data && data.sub && !data.has_contract) {
    body = (
      <div style={note}>
        {subName} has no subcontract on this job yet. Add it with its amount on {subName}’s Documents, then its lines on Billing.
        {onAddContract && <div style={{ marginTop: 8 }}><button type="button" onClick={onAddContract} style={goLink}>Add the subcontract on Documents →</button></div>}
      </div>
    );
  } else if (data && data.sub && !lines.length) {
    body = (
      <div style={note}>
        {subName} has no schedule of values lines yet. Add them on {subName}’s Billing tab.
        {onAddLines && <div style={{ marginTop: 8 }}><button type="button" onClick={onAddLines} style={goLink}>Add the lines on Billing →</button></div>}
      </div>
    );
  }

  return (
    <div>
      {error && <div style={{ marginBottom: 8, fontSize: 13.5, color: 'var(--danger)' }}>{error}</div>}
      {body || (
        <>
          {links.length === 0 && !showPicker && <div style={note}>No sub SOV links yet</div>}
          {links.map((k) => {
            const line = lines.find((l) => l.id === k.line_id);
            const value = line ? line.value_cents : 0;
            const isRemoving = removingId === k.id;
            const otherRemoving = removingId && !isRemoving;
            return (
              <div key={k.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: 'var(--bg-surface)',
                border: '1px solid var(--border-subtle)', borderRadius: 8, marginBottom: 8, opacity: otherRemoving ? 0.55 : 1 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 2 }}>
                    <span className="mono" style={{ fontSize: 13, color: 'var(--accent-text)', fontWeight: 500 }}>{numberOf.get(k.line_id) || ''}</span>
                    <span style={{ fontSize: 14.5, fontWeight: 500, color: 'var(--text-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {line ? line.description : 'A line that’s been removed'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <span className="mono" style={{ fontSize: 13.5, color: 'var(--text-1)', fontWeight: 500, letterSpacing: '-0.02em' }}>
                      {k.allocation_method === 'Percentage' && k.allocation_percent != null ? `${Math.round(k.allocation_percent * 100) / 100}%` : moneyOfCents(k.allocation_amount_cents)}
                    </span>
                    {value > 0 && (
                      <span className="mono" style={{ fontSize: 12.5, color: 'var(--text-4, var(--text-3))' }}>
                        = {moneyOfCents(k.allocation_amount_cents)} of {moneyOfCents(value)}
                      </span>
                    )}
                  </div>
                </div>
                <button type="button" onClick={() => remove(k)} disabled={isRemoving || otherRemoving} aria-label="Remove sub SOV link"
                  style={{ width: 26, height: 26, borderRadius: 6, background: 'transparent', border: 'none', color: 'var(--danger)',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: isRemoving ? 'wait' : (otherRemoving ? 'not-allowed' : 'pointer') }}
                  onMouseEnter={(e) => { if (!isRemoving && !otherRemoving) e.currentTarget.style.background = 'var(--danger-soft)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                  {isRemoving ? <Spinner size={12} color="var(--danger)" /> : <TrashIcon />}
                </button>
              </div>
            );
          })}

          {!showPicker ? (
            <div style={{ marginTop: 4 }}>
              <button type="button" onClick={() => { setShowPicker(true); setTimeout(() => searchRef.current?.focus(), 30); }}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 10px 5px 8px', borderRadius: 7, fontSize: 13.5, fontWeight: 500,
                  color: 'var(--accent-text)', background: 'var(--accent-soft)', border: '1px solid color-mix(in oklch, var(--accent) 22%, transparent)', cursor: 'pointer' }}>
                <PlusIcon />Link sub SOV line
              </button>
            </div>
          ) : (
            <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 10, padding: 12, boxShadow: 'var(--shadow-sm)', marginTop: 4 }}>
              <label style={eyebrow}>{subName}’s SOV line</label>
              <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 8, marginBottom: 12, overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderBottom: '1px solid var(--border-subtle)', color: 'var(--text-3)' }}>
                  <SearchIcon />
                  <input ref={searchRef} type="text" value={search} onChange={(e) => { setSearch(e.target.value); setLineId(''); }}
                    placeholder={`Search ${subName}’s lines…`}
                    style={{ flex: 1, minWidth: 0, background: 'transparent', border: 'none', outline: 'none', fontSize: 14.5, color: 'var(--text-1)' }} />
                </div>
                <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                  {shown.length === 0 ? (
                    <div style={{ padding: '12px 14px', fontSize: 13.5, color: 'var(--text-4, var(--text-3))', textAlign: 'center' }}>No matching lines</div>
                  ) : shown.map((l, i) => {
                    const left = leftOf(l);
                    const pctLeft = l.value_cents > 0 ? Math.max(0, (left / l.value_cents) * 100) : 100;
                    const full = left <= 0 && l.value_cents > 0;
                    const sel = String(l.id) === String(lineId);
                    const mine = links.some((k) => k.line_id === l.id);
                    return (
                      <div key={l.id}
                        onClick={() => {
                          if (full || mine) return;
                          setLineId(String(l.id));
                          setSearch(`${l.number} ${l.description}`);
                          // As SOV links: the value clamped to what the line has left.
                          if (method === 'Percentage') { if ((parseFloat(amount) || 100) > pctLeft) setAmount(String(Math.floor(pctLeft * 100) / 100)); } else if ((centsFromText(amount) || 0) > left) setAmount(moneyOfCents(left));
                        }}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderBottom: i < shown.length - 1 ? '1px solid var(--border-subtle)' : 'none',
                          background: sel ? 'var(--accent-soft)' : 'transparent', opacity: full || mine ? 0.55 : 1, cursor: full || mine ? 'not-allowed' : 'pointer' }}
                        onMouseEnter={(e) => { if (!sel && !full && !mine) e.currentTarget.style.background = 'var(--bg-hover)'; }}
                        onMouseLeave={(e) => { if (!sel && !full && !mine) e.currentTarget.style.background = 'transparent'; }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                            <span className="mono" style={{ fontSize: 13, fontWeight: 500, color: sel ? 'var(--accent-text)' : 'var(--text-3)' }}>{l.number}</span>
                            <span style={{ fontSize: 14, color: 'var(--text-1)', fontWeight: sel ? 500 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.description}</span>
                          </div>
                          <div className="mono" style={{ fontSize: 12, color: 'var(--text-4, var(--text-3))', marginTop: 2 }}>
                            {moneyOfCents(l.allocated_cents)} allocated of {moneyOfCents(l.value_cents)} · {pctLeft.toFixed(2)}% remaining
                          </div>
                        </div>
                        <div className="mono" style={{ fontSize: 12.5, fontWeight: 500, letterSpacing: '-0.02em', whiteSpace: 'nowrap',
                          color: full ? 'var(--danger)' : pctLeft < 30 ? 'var(--warning)' : 'var(--text-3)' }}>
                          {mine ? 'Linked already' : full ? 'Fully allocated' : `${moneyOfCents(left)} left`}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: over ? 10 : 12 }}>
                <div>
                  <label style={eyebrow}>Allocation type</label>
                  <div style={{ display: 'inline-flex', alignItems: 'center', height: 34, padding: 2, background: 'var(--bg-surface-2)', border: '1px solid var(--border-default)', borderRadius: 8, width: '100%' }}>
                    {['Percentage', 'Amount'].map((opt) => {
                      const active = method === opt;
                      return (
                        <button key={opt} type="button" onClick={() => { setMethod(opt); setAmount(opt === 'Percentage' ? '100' : ''); }}
                          style={{ flex: 1, height: 28, borderRadius: 6, border: 'none', fontSize: 13.5, fontWeight: 500, cursor: 'pointer',
                            color: active ? 'var(--text-1)' : 'var(--text-3)', background: active ? 'var(--bg-surface)' : 'transparent', boxShadow: active ? 'var(--shadow-xs)' : 'none' }}>
                          {opt === 'Percentage' ? 'Percentage' : 'Dollar Amount'}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <label style={eyebrow}>Value</label>
                  <div style={{ display: 'flex', alignItems: 'center', height: 34, background: 'var(--bg-surface)', borderRadius: 8, padding: '0 10px',
                    border: over ? '1px solid var(--warning)' : '1px solid var(--border-default)',
                    boxShadow: over ? '0 0 0 3px color-mix(in oklch, var(--warning) 18%, transparent)' : 'none' }}>
                    <input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" placeholder={method === 'Percentage' ? '0' : '0.00'}
                      style={{ flex: 1, minWidth: 0, width: '100%', background: 'transparent', border: 'none', outline: 'none', fontFamily: 'var(--font-mono)', fontSize: 14.5, color: 'var(--text-1)', letterSpacing: '-0.02em' }} />
                    <span style={{ color: 'var(--text-3)', fontSize: 14.5, fontFamily: 'var(--font-mono)', marginLeft: 4 }}>{method === 'Percentage' ? '%' : '$'}</span>
                  </div>
                </div>
              </div>

              {over && (
                <div style={{ padding: '10px 12px 10px 16px', background: 'var(--warning-soft)', borderRadius: 8, marginBottom: 12, boxShadow: 'inset 3px 0 0 var(--warning)',
                  display: 'flex', alignItems: 'flex-start', gap: 8, color: 'var(--warning)' }}>
                  <AlertTriangleIcon />
                  <div style={{ flex: 1, fontSize: 13.5, lineHeight: 1.45 }}>
                    <span style={{ fontWeight: 600 }}>This would exceed the line by {moneyOfCents(asked - leftOf(selected))}.</span>
                    <span style={{ display: 'block', marginTop: 2, opacity: 0.85 }}>Line {selected.number} has {moneyOfCents(leftOf(selected))} remaining. Reduce the link to fit.</span>
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button type="button" onClick={() => { reset(); setShowPicker(false); }} disabled={adding}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0 14px', height: 32, borderRadius: 8, fontSize: 14, fontWeight: 500, color: 'var(--text-2)',
                    background: 'var(--bg-surface)', border: '1px solid var(--border-default)', cursor: adding ? 'not-allowed' : 'pointer', opacity: adding ? 0.45 : 1 }}>
                  Cancel
                </button>
                <button type="button" onClick={add} disabled={!selected || !asked || over || adding}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0 16px', height: 32, borderRadius: 8, fontSize: 14, fontWeight: 500, color: '#fff',
                    background: 'var(--accent)', border: '1px solid color-mix(in oklch, var(--accent) 50%, black)',
                    boxShadow: '0 1px 0 color-mix(in oklch, var(--accent) 35%, black), 0 1px 3px oklch(0.3 0.05 135 / 0.18)',
                    cursor: adding ? 'wait' : ((!selected || !asked || over) ? 'not-allowed' : 'pointer'), opacity: (!selected || !asked || over) ? 0.45 : 1 }}>
                  {adding ? <><Spinner size={12} /> Adding…</> : 'Add Link'}
                </button>
              </div>
            </div>
          )}
        </>
      )}
      <ConfirmDialog {...confirmDialogProps} />
    </div>
  );
};

export default TaskSubSovLinks;
